import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FINISHED_RUN_STATUSES, type Run } from '@couch/shared';
import { useEffect } from 'react';
import { api, errorMessage } from '../../api/client';

export const runsKey = (recipeId: string) => ['runs', recipeId] as const;
const POLL_MS = 2000;

const STATUS_TEXT: Readonly<Record<Run['status'], string>> = {
  queued: 'Waiting in the queue…', running: 'Generating…', succeeded: 'Done', failed: 'Failed', cancelled: 'Cancelled',
};

function duration(run: Run): string {
  if (!run.finishedAt) return '';
  const s = Math.round((Date.parse(run.finishedAt) - Date.parse(run.createdAt)) / 1000);
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
}

/** One run; an unfinished one asks the server every 2 s until it is done. */
function RunRow({ initial, recipeId }: { initial: Run; recipeId: string }) {
  const queryClient = useQueryClient();
  const unfinished = !FINISHED_RUN_STATUSES.includes(initial.status);
  const live = useQuery({
    queryKey: ['run', initial.id], queryFn: () => api.getRun(initial.id), enabled: unfinished,
    refetchInterval: (query) => (query.state.data && FINISHED_RUN_STATUSES.includes(query.state.data.run.status) ? false : POLL_MS),
  });
  const run = live.data?.run ?? initial;
  const finishedNow = unfinished && FINISHED_RUN_STATUSES.includes(run.status);
  useEffect(() => {
    if (finishedNow) void queryClient.invalidateQueries({ queryKey: runsKey(recipeId) });
  }, [finishedNow, queryClient, recipeId]);
  const cancel = useMutation({ mutationFn: () => api.cancelRun(run.id), onSuccess: () => queryClient.invalidateQueries({ queryKey: runsKey(recipeId) }) });
  return (
    <li className={`run run-${run.status}`}>
      <div className="run-head">
        <span className="badge">{STATUS_TEXT[run.status]}</span>
        <span className="muted">v{run.recipeVersion} · {new Date(run.createdAt).toLocaleTimeString()} {duration(run) && `· ${duration(run)}`}</span>
        {!FINISHED_RUN_STATUSES.includes(run.status) && (
          <button type="button" className="button-quiet" disabled={cancel.isPending} onClick={() => { cancel.mutate(); }}>Cancel</button>
        )}
      </div>
      {run.error && <p className="form-error">{run.error}</p>}
      {cancel.isError && <p role="alert" className="form-error">{errorMessage(cancel.error)}</p>}
      {run.images.length > 0 && (
        <div className="run-images">
          {run.images.map((img) => (
            <figure key={img.index} className="run-image">
              {img.kind === 'video'
                // Streamed from the machine with range requests (doc 32).
                ? <video src={img.url} controls playsInline preload="metadata" aria-label={img.filename} />
                : <a href={img.url} target="_blank" rel="noreferrer"><img src={img.url} alt={img.filename} loading="lazy" /></a>}
              {/* The full-size file, saved on this device (doc 61). */}
              <figcaption><a href={`${img.url}?download=1`} download={img.filename} aria-label={`Download original ${img.filename}`}>Download original</a></figcaption>
            </figure>
          ))}
        </div>
      )}
    </li>
  );
}

export function RunsPanel({ recipeId }: { recipeId: string }) {
  const runs = useQuery({ queryKey: runsKey(recipeId), queryFn: () => api.listRuns(recipeId) });
  return (
    <section className="panel" aria-labelledby="runs-title">
      <h2 id="runs-title">Runs</h2>
      {runs.isPending && <p role="status">Loading…</p>}
      {runs.isError && <p role="alert" className="form-error">{errorMessage(runs.error)}</p>}
      {runs.data?.runs.length === 0 && <p className="muted">Not run yet.</p>}
      <ul className="run-list">{runs.data?.runs.map((r) => <RunRow key={r.id} initial={r} recipeId={recipeId} />)}</ul>
    </section>
  );
}
